import { useEffect, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/admin/ui";

const TYPES = ["Residencial", "Comercial", "Condomínio", "Industrial"];

export default function ClientsPage() {
  const [clients, setClients] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", type: TYPES[0], city: "São Paulo" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/clients", form);
      toast.success("Cliente cadastrado!");
      setForm({ name: "", phone: "", type: TYPES[0], city: "São Paulo" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-[#0A0B0D] px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none focus:border-[#FFB800]";

  if (!clients) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#FFB800]" />
      </div>
    );
  }

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clientes" sub={`${clients.length} clientes cadastrados`} testId="clients-header">
        <button
          onClick={() => setOpen(true)}
          data-testid="clients-new-button"
          className="flex items-center gap-2 rounded-full bg-[#FFB800] px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-black transition-transform hover:scale-105"
        >
          <Plus size={16} strokeWidth={3} /> Novo Cliente
        </button>
      </PageHeader>
      <TableShell head={["Nome", "Telefone", "Tipo", "Cidade", "Cliente desde"]} testId="clients-table">
        {clients.map((c, i) => (
          <tr key={c.id} className="text-zinc-300 transition-colors hover:bg-white/[0.02]" data-testid={`client-row-${i}`}>
            <td className="px-5 py-4 font-semibold text-white">{c.name}</td>
            <td className="px-5 py-4">{c.phone}</td>
            <td className="px-5 py-4">
              <span className="rounded-full border border-white/10 px-3 py-1 text-xs text-zinc-400">{c.type}</span>
            </td>
            <td className="px-5 py-4">{c.city}</td>
            <td className="px-5 py-4 text-zinc-500">{c.since}</td>
          </tr>
        ))}
      </TableShell>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-[#11141A] text-white" data-testid="client-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold uppercase">
              Novo <span className="italic text-[#FFB800]">Cliente</span>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2">
            <input required data-testid="client-name-input" placeholder="Nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <input required data-testid="client-phone-input" placeholder="Telefone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
            <select data-testid="client-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
              {TYPES.map((t) => (
                <option key={t} value={t} className="bg-[#0A0B0D]">{t}</option>
              ))}
            </select>
            <input data-testid="client-city-input" placeholder="Cidade" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
            <button type="submit" disabled={saving} data-testid="client-submit-button" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FFB800] py-3.5 font-display text-sm font-bold uppercase tracking-wider text-black transition-transform hover:scale-[1.02] disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />}
              {saving ? "Salvando..." : "Cadastrar Cliente"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
